import { BUILD_ID, BUILD_TIME, forceRefresh } from "@/lib/buildVersion";
import { getAndAdvanceChapter } from "@/lib/chapterData";
import { useVersionPoll } from "@/hooks/useVersionPoll";
import { RefreshCw, CheckCircle2, AlertTriangle } from "lucide-react";
import { useMemo, useState, useEffect } from "react";
import { useIsMobile } from "@/hooks/use-mobile";

/**
 * Green = confirmed fresh (hard reload flag present OR no newer version detected).
 * Amber = a newer build exists on the server (detected via build-meta.json polling).
 */
function useIsLatestVersion() {
  const [confirmedFresh, setConfirmedFresh] = useState(false);
  const { isStale } = useVersionPoll(60_000);

  useEffect(() => {
    const wasHardReload = sessionStorage.getItem("perform_was_hard_reload");
    if (wasHardReload) {
      setConfirmedFresh(true);
      sessionStorage.removeItem("perform_was_hard_reload");
    }
  }, []);

  // If polling detects a newer version, override even a confirmed-fresh state
  if (isStale) return { status: "stale" as const };
  if (confirmedFresh) return { status: "fresh" as const };
  // No hard reload flag AND no stale signal → neutral (we can't confirm)
  return { status: "unknown" as const };
}

export default function BuildBadge() {
  const isMobile = useIsMobile();
  const [refreshing, setRefreshing] = useState(false);
  const { status } = useIsLatestVersion();
  const chapter = useMemo(() => getAndAdvanceChapter(BUILD_ID), []);

  // Hidden on mobile — replaced by MobileFloatingDock
  if (isMobile) return null;

  const handleRefresh = async () => {
    setRefreshing(true);
    await forceRefresh();
  };

  const isFresh = status === "fresh";
  const isStale = status === "stale";

  return (
    <div
      className={`fixed bottom-2 right-2 z-50 flex items-center gap-2 rounded-xl backdrop-blur-sm border px-3 py-1.5 select-none transition-colors duration-300 ${
        isStale
          ? "bg-amber-950/80 border-amber-600/50"
          : isFresh
            ? "bg-emerald-950/80 border-emerald-700/50"
            : "bg-card/80 border-border"
      }`}
    >
      {isStale ? (
        <AlertTriangle className="h-3.5 w-3.5 text-amber-400 shrink-0 animate-pulse" />
      ) : isFresh ? (
        <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400 shrink-0" />
      ) : null}
      <div className="min-w-0">
        <span
          className={`block text-xs font-semibold truncate capitalize ${
            isStale
              ? "text-amber-300"
              : isFresh
                ? "text-emerald-300"
                : "text-foreground"
          }`}
        >
          {chapter.chapter}
        </span>
        <span
          className={`block text-[9px] truncate ${
            isStale
              ? "text-amber-400/70"
              : isFresh
                ? "text-emerald-400/70"
                : "text-muted-foreground"
          }`}
        >
          {chapter.arc} · V{chapter.version}
        </span>
      </div>
      <button
        onClick={handleRefresh}
        disabled={refreshing}
        className={`ml-0.5 p-1 rounded-md transition-colors disabled:opacity-50 ${
          isStale
            ? "text-amber-400 hover:bg-amber-800/50"
            : isFresh
              ? "text-emerald-400 hover:bg-emerald-800/50"
              : "text-muted-foreground hover:bg-muted/50"
        }`}
        aria-label="Actualiser le site"
        title={isStale ? "Nouvelle version disponible — cliquer pour mettre à jour" : "Forcer la mise à jour"}
      >
        <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : isStale ? "animate-pulse" : ""}`} />
      </button>
    </div>
  );
}
